import { Alert, Box, Button, Grid, Snackbar, Typography } from "@mui/material";
import { Editor } from "@tinymce/tinymce-react";
import { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { useUpdateTenancyMutation } from "../../store/services/tenancy.service";
import { useGetTinyJwtTokenQuery, useLazyGetTinyJwtTokenQuery } from "../../store/services/simulation-service";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useLocation } from 'react-router-dom';
import ColorPicker from "../../components/ColorPicker";
import {
    isLandingPageColour,
    readLandingPageBackground,
    writeLandingPageBackground
} from "../landing-page/landingPageBackground";

// The editor never sees the background marker: it is split off here and added back on save.
function splitBackground(data) {
    const { backgroundColor, html } = readLandingPageBackground(data?.landingPage);
    return { backgroundColor, tenancyData: data && { ...data, landingPage: html } };
}

// Shows the chosen colour behind the editor content so admins can judge contrast while writing.
function showEditorBackground(editor, colour) {
    const body = editor?.getBody();
    if (body) {
        body.style.backgroundColor = isLandingPageColour(colour) ? colour : "";
    }
}

DefaultLandingPage.propTypes = {
    data: PropTypes.object.isRequired
};

export default function DefaultLandingPage(props) {
    const { data } = props;
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const lightMode = theme.palette.mode === "light";

    // Create local state for the landing page content
    const [tenancyData, setTenancyData] = useState(() => splitBackground(data).tenancyData);
    const [backgroundColor, setBackgroundColor] = useState(() => splitBackground(data).backgroundColor);
    const editorRef = useRef(null);
    const [updateTenancy] = useUpdateTenancyMutation();

    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");
    const [triggerJwtToken, { data: jwtToken, isLoading: jwtTokenLoading, error: jwtTokenError }] = useLazyGetTinyJwtTokenQuery();

    // Update local state when props change
    useEffect(() => {
        const split = splitBackground(data);
        setTenancyData(split.tenancyData);
        setBackgroundColor(split.backgroundColor);
    }, [data]);

    useEffect(() => {
        showEditorBackground(editorRef.current, backgroundColor);
    }, [backgroundColor]);

    function handleSave() {
        if (backgroundColor && !isLandingPageColour(backgroundColor)) {
            setSnackbarMessage("Background colour must be a hex value such as #1a2b3c.");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            return;
        }

        const landingPage = writeLandingPageBackground(tenancyData.landingPage, backgroundColor);
        // unwrap() so a failed save rejects into the catch below instead of reporting success.
        updateTenancy({ data: { ...tenancyData, landingPage }, tenancyId: tenancyData.tenancyId })
            .unwrap()
            .then(() => {
                setSnackbarMessage("Default landing page settings updated successfully.");
                setSnackbarSeverity("success");
                setSnackbarOpen(true);
            })
            .catch((error) => {
                console.error("Error updating default landing page settings:", error);
                setSnackbarMessage("Error updating default landing page settings.");
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
            });
    }

    const handleEditorChange = (newContent) => {
        setTenancyData(prev => ({
            ...prev,
            landingPage: newContent
        }));
    };

    const editorStyle = `
        body { 
            background-color: ${lightMode ? 'white' : theme.palette.black.main}; 
            color: ${lightMode ? 'black' : theme.palette.grey[50]}; 
        }
    `;

    const tinydriveTokenProvider = useCallback((callback) => {
        const getToken = async () => {
            try {
                const response = await triggerJwtToken().unwrap();
                if (response) {
                    callback(response);
                } else {
                    console.warn("JWT token not available in the response");
                    callback(null);
                }
            } catch (error) {
                console.error("Error fetching JWT token:", error);
                callback(null);
            }
        };

        getToken();
    }, [triggerJwtToken]);

    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
        .tox-tinymce {
            border-color: ${lightMode ? theme.palette.grey[200] : theme.palette.black.light} !important;
        }
        .tox .tox-statusbar {
            background-color: ${lightMode ? 'inherit' : theme.palette.black.light} !important;
            color: ${lightMode ? 'inherit' : theme.palette.grey[300]} !important;
        }
    `;
        document.head.appendChild(style);
        return () => {
            document.head.removeChild(style);
        };
    }, [theme, lightMode]);

    if (tenancyData) {
        return (
            <>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Box sx={{ display: "flex", alignItems: "flex-end", gap: 2 }}>
                            <ColorPicker
                                label="Page background"
                                color={backgroundColor || "#ffffff"}
                                onChange={setBackgroundColor}
                            />
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {backgroundColor ? "Colour behind the page content." : "No colour set: the page uses the default background."}
                                </Typography>
                                <Button
                                    variant="outlined"
                                    color="black"
                                    disabled={!backgroundColor}
                                    onClick={() => setBackgroundColor("")}
                                >
                                    Reset to default
                                </Button>
                            </Box>
                        </Box>
                    </Grid>

                    <Grid item xs={12}>
                        <Box>
                            <Editor
                                apiKey="j2g2hy77ifpdh2oe7hfarnk6vvls82qlv49guynyzw6d22ha"
                                value={tenancyData.landingPage}
                                onEditorChange={handleEditorChange}
                                onInit={(event, editor) => {
                                    editorRef.current = editor;
                                    showEditorBackground(editor, backgroundColor);
                                }}
                                init={{
                                    skin: lightMode ? 'oxide' : 'oxide-dark',
                                    content_css: lightMode ? 'default' : 'dark',
                                    content_style: editorStyle,
                                    // Landing pages are authored by trusted admins and embed
                                    // redirect iframes (e.g. LMS deeplinks) that must run scripts
                                    // and navigate the top window. TinyMCE 7 sandboxes iframes by
                                    // default (sandbox=""), which blocks both and breaks the
                                    // redirect. Opt this trusted content out of that default.
                                    sandbox_iframes: false,
                                    selector: ".tinymce",
                                    editable_class: 'mceEditable',
                                    plugins: [
                                        "advlist", "autolink", "lists", "link", "image", "charmap", "preview", "anchor",
                                        "searchreplace", "visualblocks", "code", "fullscreen",
                                        "insertdatetime", "media", "table", "code", "help", "wordcount", "template", "tinydrive"
                                    ],
                                    images_file_types: "jpg,png,jpeg,webp",
                                    tinydrive_token_provider: tinydriveTokenProvider,
                                    toolbar:
                                        "template image | code | undo redo | fontselect fontsize fontsizeselect bold italic forecolor backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link",
                                    templates: [
                                        {
                                            title: "User name",
                                            description: "Insert the users full name",
                                            content: "{user.name}"
                                        }
                                    ],
                                    height: "75vh",
                                    promotion: false,
                                    file_picker_types: 'media image'
                                }}
                            />
                        </Box>
                    </Grid>

                    <Grid item xs={12}>
                        <Button
                            variant="outlined"
                            color="black"
                            onClick={() => navigate(location.from ? location.from : -1)}
                            sx={{ width: "10%" }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSave}
                            variant="contained"
                            color="warning"
                            sx={{ ml: 2, width: "10%" }}
                        >
                            Save
                        </Button>
                    </Grid>
                </Grid>

                <Snackbar
                    anchorOrigin={{ vertical: "top", horizontal: "center" }}
                    open={snackbarOpen}
                    autoHideDuration={3000}
                    onClose={() => setSnackbarOpen(false)}
                >
                    <Alert
                        onClose={() => setSnackbarOpen(false)}
                        severity={snackbarSeverity}
                        variant="filled"
                        sx={{ width: "100%" }}
                    >
                        {snackbarMessage}
                    </Alert>
                </Snackbar>
            </>
        );
    }
}